import React, { FC, useEffect, useState } from 'react'
import {
  ActionButton,
  ActionContainer,
  CompletedSupervisionEmpty,
  CompletedSupervisionItem,
  CompletedSupervisionItemWrapper,
  CompletedSupervisionLabel,
  CompletedSupervisionList,
  CompletionStatusWrapper,
  FileAction,
  FileWrapper,
  ItemLabel,
  ItemStatusDate,
  Title,
} from './SupervisionManagementModal.styles'
import { ExternalLinkIcon } from 'uiKit/icons/ExternalLinkIcon'
import { DownloadIcon } from 'uiKit/icons/DownloadIcon'
import { getSessionsHistory } from '../../api/usersApi'
import { UserType } from '../../model/usersModel'
import LoaderScreen from '../../../../uiKit/loaders/loaderScreen'
import { downloadSupervisionResultHandler, downloadSupervisionTranscript } from '../../helpers/supervisionFileHelper'
import { Typography } from '@material-ui/core'
import { BOT_ID, TEMPLATE_ID } from 'constants/numeric'

interface Props {
  botId: string
  selectedUser: UserType
}

export const CompletedSupervisionTab: FC<Props> = ({ botId, selectedUser }) => {
  const [completedSupervisions, setCompletedSupervisions] = useState([])
  const [isLoadingCompletedSupervision, setIsLoadingCompletedSupervision] = useState(true)

  useEffect(() => {
    const controller = new AbortController()
    const signal = controller.signal
    getSessionsHistory(botId, selectedUser.id, signal).then(completedSupervisions => {
      setCompletedSupervisions(completedSupervisions)
      setIsLoadingCompletedSupervision(false)
    })

    return () => {
      controller.abort()
    }
  }, [])

  return (
    <>
      {isLoadingCompletedSupervision ? (
        <LoaderScreen
          size={{
            circleSize: 60,
            border: 8,
          }}
        />
      ) : (
        <>
          <CompletedSupervisionLabel>
            Completed supervisions are stored for 24 months after the date of completion.
          </CompletedSupervisionLabel>
          {!!completedSupervisions.length ? (
            <CompletedSupervisionList>
              {completedSupervisions.map((supervision, index) => (
                <CompletedSupervisionItemWrapper key={index}>
                  <Typography
                    style={{
                      fontWeight: 700,
                      fontSize: '18px',
                      marginBottom: '5px',
                      color: 'var(--color-charcoal)',
                    }}>
                    {supervision?.template?.name}
                  </Typography>
                  <CompletedSupervisionItem>
                    <CompletionStatusWrapper>
                      <ItemLabel>Date of completion</ItemLabel>
                      <ItemStatusDate>{new Date(supervision.completedAt).toLocaleDateString('en-GB')}</ItemStatusDate>
                    </CompletionStatusWrapper>

                    {botId === BOT_ID && supervision.template.id === TEMPLATE_ID ? null : (
                      <FileWrapper>
                        <ItemLabel>Supervision results</ItemLabel>
                        <FileAction>
                          <Title maxWidth={65}>{supervision.outputFormFileName}</Title>
                          <ActionContainer>
                            <ActionButton
                              onClick={() =>
                                downloadSupervisionResultHandler(
                                  botId,
                                  supervision.id,
                                  supervision.outputFormFileName,
                                  false,
                                )
                              }>
                              <DownloadIcon />
                            </ActionButton>
                            <ActionButton
                              onClick={() =>
                                downloadSupervisionResultHandler(
                                  botId,
                                  supervision.id,
                                  supervision.outputFormFileName,
                                  true,
                                )
                              }>
                              <ExternalLinkIcon />
                            </ActionButton>
                          </ActionContainer>
                        </FileAction>
                      </FileWrapper>
                    )}

                    <FileWrapper>
                      <ItemLabel>Supervision transcript</ItemLabel>
                      <FileAction>
                        <Title maxWidth={65}>{supervision.transcriptFileName}</Title>
                        <ActionContainer>
                          <ActionButton
                            onClick={() =>
                              downloadSupervisionTranscript(
                                botId,
                                supervision.id,
                                supervision.transcriptFileName,
                                false,
                              )
                            }>
                            <DownloadIcon />
                          </ActionButton>
                          <ActionButton
                            onClick={() =>
                              downloadSupervisionTranscript(botId, supervision.id, supervision.transcriptFileName, true)
                            }>
                            <ExternalLinkIcon />
                          </ActionButton>
                        </ActionContainer>
                      </FileAction>
                    </FileWrapper>
                  </CompletedSupervisionItem>
                </CompletedSupervisionItemWrapper>
              ))}
            </CompletedSupervisionList>
          ) : (
            <CompletedSupervisionEmpty>No completed supervisions available.</CompletedSupervisionEmpty>
          )}
        </>
      )}
    </>
  )
}
